import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConsentStep, partnerNameFromSnapshot } from '../flow/ConsentStep';
import i18n from 'i18next';
import type { TenantBranding } from '../flow/types';
import { applyCustomization } from '../flow/customization';
import { parseBrandingMessage } from '../preview';
import { fromParent, postToParent } from '../preview/parent';

const NOOP = () => {};

/**
 * `/preview/consent` — the applicant's consent screen with the branding the
 * console is editing, framed by the console's "Verification screen" settings.
 *
 * The real `ConsentStep`, in the same card the live flow mounts it in
 * (`FlowRunner`), so the preview cannot drift from the screen it shows. It is
 * rendered `inert`: Agree would otherwise prompt the operator for their
 * location, and nothing on a preview should act.
 */
export function PreviewScreen() {
  const { t } = useTranslation();
  const [branding, setBranding] = useState<TenantBranding | null>(null);
  const mainRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!fromParent(event)) return;
      const message = parseBrandingMessage(event.data);
      if (!message) return;
      setBranding(message.branding);
      // The project's look and copy, as the console is editing it.
      applyCustomization(message.branding, i18n);
      // The global instance (`src/i18n` initializes it), not the hook's `i18n`:
      // that one changes identity with the language, and a listener keyed on it
      // would re-subscribe — and re-announce ready, prompting a resend — on
      // every switch.
      if (message.language && message.language !== i18n.language) {
        void i18n.changeLanguage(message.language);
      }
    };
    window.addEventListener('message', onMessage);
    // After subscribing, so the console's first reply cannot arrive unheard.
    postToParent({ type: 'trustsy:preview-ready' });
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Report the page height so the console can size the frame to fit — the
  // consent card is taller on a narrow viewport, where Thai copy wraps.
  useEffect(() => {
    const report = () =>
      postToParent({
        type: 'trustsy:preview-size',
        height: Math.ceil(document.documentElement.scrollHeight),
      });
    const observer = new ResizeObserver(report);
    if (mainRef.current) observer.observe(mainRef.current);
    report();
    return () => observer.disconnect();
  }, []);

  return (
    <main className="screen" ref={mainRef}>
      <p className="preview-ribbon" role="note">
        {t('preview.ribbon')}
      </p>
      <div className="card" inert>
        <ConsentStep
          branding={branding}
          onAgree={NOOP}
          partnerName={partnerNameFromSnapshot({ branding: branding ?? emptyBranding })}
        />
      </div>
    </main>
  );
}

const emptyBranding: TenantBranding = {
  display_name: null,
  logo_url: null,
  privacy_url: null,
  terms_url: null,
  website_url: null,
};
